export type BackupProgress = {
  revision: number;
  lastRestoreId?: string;
  lastBackedUpRestoreId?: string;
  pendingSince: number | null;
  lastChangeAt: number | null;
  dueAt: number | null;
  capturedRevision: number | null;
  afterCaptureAt: number | null;
  lastCheckedAt: number | null;
  lastCompletedAt: number | null;
};
export const initialBackupProgress = (): BackupProgress => ({
  revision: 0,
  pendingSince: null,
  lastChangeAt: null,
  dueAt: null,
  capturedRevision: null,
  afterCaptureAt: null,
  lastCheckedAt: null,
  lastCompletedAt: null,
});
export function backupChanged(state: BackupProgress, now: number): BackupProgress {
  const first = state.pendingSince ?? now;
  return {
    ...state,
    revision: state.revision + 1,
    pendingSince: first,
    lastChangeAt: now,
    dueAt: state.dueAt !== null && state.dueAt <= now ? state.dueAt : Math.min(now + 5 * 60000, first + 30 * 60000),
    afterCaptureAt: state.capturedRevision === null ? null : (state.afterCaptureAt ?? now),
  };
}
export function backupCompleted(state: BackupProgress, revision: number, now: number): BackupProgress {
  if (revision > state.revision || revision < 0) throw new Error("backup-revision-invalid");
  const pendingSince = revision === state.revision ? null : (state.afterCaptureAt ?? state.pendingSince);
  return {
    ...state,
    pendingSince,
    dueAt:
      pendingSince === null
        ? null
        : Math.min((state.lastChangeAt ?? pendingSince) + 5 * 60000, pendingSince + 30 * 60000),
    capturedRevision: null,
    afterCaptureAt: null,
    lastCompletedAt: now,
    lastCheckedAt: now,
  };
}
export const backupDue = (state: BackupProgress, now: number) => state.dueAt !== null && now >= state.dueAt;
const hkDay = (time: number) => Math.floor((time + 8 * 3600000) / 86400000);
export function dailyBackupDue(lastCheckedAt: number | null, now: number) {
  const day = hkDay(now);
  const todayCheck = day * 86400000 + 8 * 3600000;
  const due = now >= todayCheck ? todayCheck : todayCheck - 86400000;
  return lastCheckedAt === null || lastCheckedAt < due;
}

export type BackupVersion = {
  id: string;
  workspaceId: string;
  deviceId: string;
  revision: number;
  datasetId: string | null;
  generation: string | null;
  clock: Record<string, number>;
  contentHash: string;
  dataAt: number;
  completedAt: number;
  kind: "automatic" | "manual";
  protected?: boolean;
  restoreId?: string;
};
export function backupCovers(newer: BackupVersion, older: BackupVersion) {
  if (newer.workspaceId !== older.workspaceId) return false;
  return newer.contentHash === older.contentHash || backupHistoryCovers(newer, older);
}
// Compare causal history independently of an accidental return to old content.
export function backupHistoryCovers(newer: BackupVersion, older: BackupVersion) {
  if (newer.workspaceId !== older.workspaceId) return false;
  if (newer.datasetId !== older.datasetId || newer.generation !== older.generation) return false;
  if (!newer.datasetId) return newer.deviceId === older.deviceId && newer.revision > older.revision;
  const keys = new Set([...Object.keys(newer.clock), ...Object.keys(older.clock)]);
  return (
    [...keys].every((key) => (newer.clock[key] ?? 0) >= (older.clock[key] ?? 0)) &&
    [...keys].some((key) => (newer.clock[key] ?? 0) > (older.clock[key] ?? 0))
  );
}
function latestSafe(files: BackupVersion[]) {
  // Topological ordering avoids a late stale upload outranking its successor.
  const remaining = [...files];
  const ordered: BackupVersion[] = [];
  while (remaining.length) {
    const heads = remaining.filter(
      (file) =>
        !remaining.some((other) => other.id !== file.id && backupCovers(other, file) && !backupCovers(file, other)),
    );
    heads.sort((a, b) => b.completedAt - a.completedAt || a.id.localeCompare(b.id));
    const next = heads[0];
    if (!next) throw new Error("backup-coverage-invalid");
    ordered.push(next);
    remaining.splice(remaining.indexOf(next), 1);
  }
  return ordered;
}
export function selectBackupRetention(files: BackupVersion[], _now: number) {
  const automatic = files.filter((file) => file.kind === "automatic");
  const keep = new Set(files.filter((file) => file.kind !== "automatic" || file.protected).map((file) => file.id));
  const daily = new Set<string>();
  const recent = new Set<string>();
  const groups = new Map<string, BackupVersion[]>();
  for (const file of automatic) {
    const group = groups.get(file.workspaceId) ?? [];
    group.push(file);
    groups.set(file.workspaceId, group);
  }
  for (const group of groups.values()) {
    for (const file of latestSafe(group).slice(0, 4)) {
      keep.add(file.id);
      recent.add(file.id);
    }
    for (const file of group) {
      const covered = group.some(
        (other) =>
          other.id !== file.id &&
          backupCovers(other, file) &&
          (!backupCovers(file, other) ||
            other.completedAt > file.completedAt ||
            (other.completedAt === file.completedAt && other.id < file.id)),
      );
      if (!covered) keep.add(file.id);
    }
  }
  return {
    keep: [...keep],
    daily: [...daily],
    recent: [...recent],
    remove: automatic.filter((file) => !keep.has(file.id)).map((file) => file.id),
  };
}
